/* eslint react/prop-types: 0 */
import { useState, useCallback } from 'react';
import clsx from 'clsx';
import { Box, Button, CircularProgress, Menu, MenuItem, Typography } from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { saveAs } from 'file-saver';
import { Trans } from '@lingui/macro';
import _ from 'lodash';
import WebTestNowResults from './WebTestNowResults';
import { useResultStyles } from './WebTestNowStyles';
import useIsPrinting from '../../../utilities/PrintUtilities';

const CSV_SEPARATOR = '\t';
const CSV_EOL = '\r\n';
const FILE_TYPE_STRING = 'text/plain;charset=utf-8';

function csvClean(value) {
    if (value === null || value === undefined) return '';
    return String(value).replace(/[\t\r\n]/g, ' ');
}

function csvGet(object, key) {
    const val = _.get(object, key);
    return val ? csvClean(val) : '';
}

function csvLocation(locationObject) {
    const city = csvGet(locationObject, 'city');
    if (city) {
        const country = csvGet(locationObject, 'country');
        return country ? `${city}, ${country}` : city;
    }
    return csvGet(locationObject, 'country');
}

function buildCSVDetailHeaders() {
    return [
        'Name',
        'Hostname',
        'Internal IP',
        'Destination',
        'Test',
        'Pass/Fail',
        'HTTP Response Code',
        'Response Size',
        'Response Time',
        'Redirect Time',
        'Network Delay',
        'SSL Delay',
        'Client Delay',
        'Application Delay',
        'Server Delay',
    ].join(CSV_SEPARATOR);
}

function buildCSVDetailData(test, agentsById) {
    const rows = [];
    (test?.agentResults || []).forEach((result) => {
        const agent = agentsById[result.agent];
        const agentArray = [
            csvGet(agent, 'name'),
            csvGet(agent, 'network.hostname'),
            csvGet(agent, 'network.ipv4Local'),
        ];
        const destination = _.get(result, 'traceRoute.destination', '');
        if (destination) agentArray.push(destination);

        const webResults = (result.results || []).filter((r) => r?.resultType === 'web' || r?.data);
        webResults.forEach((rawData, idx) => {
            const stats = rawData.data || {};
            const row = [
                ...agentArray,
                idx + 1,
                stats.is_pass ? 'Pass' : 'Fail',
                csvGet(stats, 'html_response_code'),
                csvGet(stats, 'response_size'),
                (csvGet(stats, 'response_time') || 0) / 1000,
                (csvGet(stats, 'namelookup_time') || 0) / 1000,
                (csvGet(stats, 'network_delay') || 0) / 1000,
                (csvGet(stats, 'ssl_delay') || 0) / 1000,
                (csvGet(stats, 'client_delay') || 0) / 1000,
                (csvGet(stats, 'app_delay') || 0) / 1000,
                (csvGet(stats, 'server_delay') || 0) / 1000,
            ];
            rows.push(row.join(CSV_SEPARATOR));
        });
    });
    return rows.join(CSV_EOL);
}

function buildCSVTraceHeaders() {
    return [
        'Name',
        'Hostname',
        'Internal IP',
        'Destination',
        'Hop',
        'IP',
        'DNS',
        'Organization',
        'Location',
        'ASN',
        'Response 1',
        'Response 2',
        'Response 3',
    ].join(CSV_SEPARATOR);
}

function buildCSVTraceData(test, agentsById) {
    const rows = [];
    (test?.agentResults || []).forEach((result) => {
        const agent = agentsById[result.agent];
        const traceObject = result.traceRoute || {};
        const locations = _.keyBy(_.get(traceObject, 'geoData'), 'ip');
        const agentArray = [
            csvGet(agent, 'name'),
            csvGet(agent, 'network.hostname'),
            csvGet(agent, 'network.ipv4Local'),
        ];
        const destination = _.get(traceObject, 'destination', '');
        if (destination) agentArray.push(destination);

        (traceObject.hop_data || []).forEach((hop, index) => {
            const locationObject = locations[hop.ip];
            const row = [
                ...agentArray,
                index + 1,
                csvGet(hop, 'ip'),
                csvGet(hop, 'dns'),
                csvGet(hop, 'as_org') || csvGet(hop, 'isp'),
                csvLocation(locationObject),
                csvGet(hop, 'asn'),
            ];
            (hop.response_times || []).forEach((time) => {
                row.push(time === '0ms' ? '<1ms' : time || '');
            });
            rows.push(row.join(CSV_SEPARATOR));
        });
    });
    return rows.join(CSV_EOL);
}

export function WebTestNowExportMenu({ test, agentsById, disabled }) {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleOpen = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handleExportDetail = useCallback(() => {
        handleClose();
        const content = `${buildCSVDetailHeaders()}${CSV_EOL}${buildCSVDetailData(test, agentsById)}`;
        saveAs(new Blob([content], { type: FILE_TYPE_STRING }), 'web_test_now_test_log.txt', { autoBom: true });
    }, [test, agentsById]);

    const handleExportTrace = useCallback(() => {
        handleClose();
        const content = `${buildCSVTraceHeaders()}${CSV_EOL}${buildCSVTraceData(test, agentsById)}`;
        saveAs(new Blob([content], { type: FILE_TYPE_STRING }), 'web_test_now_path_details.txt', { autoBom: true });
    }, [test, agentsById]);

    const noTest = !test;
    const isDisabled = disabled || noTest;

    return (
        <>
            <Button size="small" disabled={isDisabled} onClick={handleOpen} endIcon={<ArrowDropDownIcon />}>
                Export
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
                <MenuItem onClick={handleExportDetail}>Test Details</MenuItem>
                <MenuItem onClick={handleExportTrace}>Path Details</MenuItem>
            </Menu>
        </>
    );
}

const HeaderNoPrint = ({ results }) => {
    if (!results?.test?.name) return null;

    return (
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            style={{ padding: '0 16px 2px', borderRadius: 4, position: 'relative' }}
        >
            {results?.showLoading && <CircularProgress size={20} style={{ position: 'absolute', top: 4, left: 8 }} />}
            <Box style={{ textAlign: 'center', marginLeft: 80, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Typography variant="body1" style={{ lineHeight: 1.2 }}>
                    {results?.status}
                </Typography>
            </Box>
            <Box style={{ textAlign: 'center' }}>
                <Typography variant="h6" style={{ margin: 0, lineHeight: 1.2 }}>
                    {results?.test?.name}
                </Typography>
                <Typography variant="h6" style={{ margin: 0, lineHeight: 1.2 }}>
                    <a target="_blank" rel="noreferrer" href={results?.test?.testConfig?.url}>
                        {results?.test?.testConfig?.url}
                    </a>
                </Typography>
            </Box>
            <Box style={{ textAlign: 'center' }}>
                <Typography variant="body1" style={{ lineHeight: 1.2 }}>
                    {results?.createdAtFmt}
                </Typography>
            </Box>
        </Box>
    );
};

const HeaderPrint = ({ results }) => {
    if (!results?.test?.name) return null;

    return (
        <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            style={{ width: '100%', padding: '10px 10px 16px', marginBottom: 6 }}
        >
            <Box style={{ fontSize: 14, lineHeight: '22px' }}>
                <div>
                    <strong>
                        <Trans>Test Now Name</Trans>
                    </strong>
                    : {results?.test?.name}
                </div>
                <div>
                    <strong>
                        <Trans>Status</Trans>
                    </strong>
                    : {results?.status}
                    {results?.timedOut === true && (
                        <>
                            {' '}
                            - <Trans>Timeout</Trans>
                        </>
                    )}
                </div>
                <div>
                    <strong>
                        <Trans>Date</Trans>
                    </strong>
                    : {results?.createdAtFmt}
                </div>
                <div>{results?.test?.testConfig?.url}</div>
            </Box>
            <Box style={{ paddingRight: 20 }}>
                <img src="/images/ntct-logo-footer.png" alt="Netscout" title="netscout icon" />
            </Box>
        </Box>
    );
};

export default function WebTestNowResultsMainPanel({ results, agentsById, setAgentsById }) {
    const classes = useResultStyles();
    const isPrinting = useIsPrinting();
    const [localAgentsById, setLocalAgentsById] = useState({});
    const effectiveAgentsById = agentsById || localAgentsById;
    const effectiveSetAgentsById = setAgentsById || setLocalAgentsById;

    return (
        <div className={clsx('flex-container', classes.fadeOnHoverTrigger)}>
            <div className={classes.static}>
                {isPrinting ? <HeaderPrint results={results} /> : <HeaderNoPrint results={results} />}
                <WebTestNowResults
                    classes={classes}
                    test={results?.test}
                    agentsById={effectiveAgentsById}
                    setAgentsById={effectiveSetAgentsById}
                />
            </div>
        </div>
    );
}

WebTestNowResultsMainPanel.defaultProps = {
    chartHeight: 150,
};
